/**
 * Utility functions for pet-related data transformations
 */

import { humanizeLineItemCode } from "./data";

// List of all bad habits options
export const ALL_BAD_HABITS = [
  'demandBarking', 'destructiveChewing', 'excessiveBarking', 'preyDrive',
  'reactivityBarrier', 'reactivityOtherDogs', 'reactivityPeople', 'reactivityLeash',
  'resourceGuardingFood', 'resourceGuardingPeople', 'resourceGuardingToys',
  'generalAnxiety', 'separationAnxiety', 'velcro'
];

/**
 * Transform individual bad habits fields to array format
 * @param {Object} petData - Pet data object containing individual bad habits fields
 * @param {string} fieldPrefix - Prefix for the field names (e.g., 'badHabits_' or 'bad_habits_')
 * @returns {Array} Array of selected bad habits
 */
export const transformBadHabitsToArray = (petData, fieldPrefix = 'badHabits_') => {
  const badHabitsArray = [];
  
  // Check for "None" option
  if (petData[`${fieldPrefix}none`]) {
    badHabitsArray.push('none');
  }
  
  // Check for "Other" option
  if (petData[`${fieldPrefix}other`]) {
    badHabitsArray.push('other');
  }
  
  // Check all sliding scale habits
  ALL_BAD_HABITS.forEach(habit => {
    const fieldName = `${fieldPrefix}${habit}`;
    if (petData[fieldName] && petData[fieldName] > 0) {
      badHabitsArray.push(habit);
    }
  });
  
  return badHabitsArray;
};

/**
 * Transform array format bad habits to individual fields
 * @param {Array|string} badHabitsData - Bad habits in array format or JSON string
 * @param {string} fieldPrefix - Prefix for the field names (e.g., 'badHabits_' or 'bad_habits_')
 * @param {string} otherText - Additional text for "Other" option
 * @returns {Object} Object with individual bad habits fields
 */
export const transformBadHabitsToFields = (badHabitsData, fieldPrefix = 'badHabits_', otherText = '') => {
  const transformedFields = {};
  
  // Initialize all fields to 0 or false
  ALL_BAD_HABITS.forEach(habit => {
    transformedFields[`${fieldPrefix}${habit}`] = 0;
  });
  transformedFields[`${fieldPrefix}none`] = false;
  transformedFields[`${fieldPrefix}other`] = false;
  transformedFields[`${fieldPrefix}otherText`] = '';
  
  // Parse the bad habits data
  let badHabitsArray = [];
  if (typeof badHabitsData === 'string') {
    try {
      badHabitsArray = JSON.parse(badHabitsData);
    } catch (e) {
      console.warn('Failed to parse bad habits JSON:', e);
      badHabitsArray = [];
    }
  } else if (Array.isArray(badHabitsData)) {
    badHabitsArray = badHabitsData;
  }
  
  // Set values based on the array
  if (Array.isArray(badHabitsArray)) {
    badHabitsArray.forEach(habit => {
      if (habit === 'none') {
        transformedFields[`${fieldPrefix}none`] = true;
      } else if (habit === 'other') {
        transformedFields[`${fieldPrefix}other`] = true;
        transformedFields[`${fieldPrefix}otherText`] = otherText || '';
      } else if (ALL_BAD_HABITS.includes(habit)) {
        // For now, set to intermediate value (2) if it was selected
        // In the future, we might need to store the actual slider values
        transformedFields[`${fieldPrefix}${habit}`] = 2;
      }
    });
  }
  
  return transformedFields;
};

/**
 * Get the "Other" text field name based on the field prefix
 * @param {string} fieldPrefix - Prefix for the field names
 * @returns {string} The "Other" text field name
 */
export const getOtherTextFieldName = (fieldPrefix = 'badHabits_') => {
  return `${fieldPrefix}otherText`;
};

/**
 * Get the "Other" checkbox field name based on the field prefix
 * @param {string} fieldPrefix - Prefix for the field names
 * @returns {string} The "Other" checkbox field name
 */
export const getOtherCheckboxFieldName = (fieldPrefix = 'badHabits_') => {
  return `${fieldPrefix}other`;
};

/**
 * Get the "None" checkbox field name based on the field prefix
 * @param {string} fieldPrefix - Prefix for the field names
 * @returns {string} The "None" checkbox field name
 */
export const getNoneCheckboxFieldName = (fieldPrefix = 'badHabits_') => {
  return `${fieldPrefix}none`;
};

export const formatPetType = (code) => {
  if (code.includes('-dogs')) return 'dog';
  if (code.includes('-puppies')) return 'puppy';
  if (code.includes('-cats')) return 'cat';
  return 'pet';
};

export const formatPricingType = (code) => {
  let pricingType = 'Standard';
  if (code.includes('holiday')) pricingType = 'Holiday';

  let service_type = code.includes('in-office') 
    ? 'In Office Coverage:' 
    : pricingType === 'holiday' ? '' : 'Standard';

  if (code.includes('base-')) {
    return pricingType === 'holiday' ? `Holiday ${service_type}` : service_type;
  } else if (code.includes('additional-')) {
    return pricingType === 'holiday' ? `Holiday ${service_type}` : service_type;
  }
  return '';
};

export const formatPetCount = (petCount, petType) => {
  if (petCount === 1) {
    return `1 ${petType}`;
  }
  return `${petCount} ${petType}s`;
};

export const formatDuration = (quantity, unitType = 'day') => {
  const days = parseInt(quantity ? quantity.toString() : '0', 10) || 0;
  
  if (days <= 0) return '';

  const unit = unitType;
  const unitPlural = unit + 's';
  const durationString = days === 1 ? `1 ${unit}` : `${days} ${unitPlural}`;
  
  return ` × ${durationString}`;
};

/**
 * Generates the human-readable label for a pet line item.
 * e.g. "Standard 1 cat x 2 days"
 * * @param {string} code - The line item code
 * @param {number} seats - The total number of pets (seats)
 * @param {number|string} quantity - The duration quantity
 * @param {string} unitType - 'day', 'night', or 'hour'
 */
export const getPetLineItemLabel = (code, seats, quantity, unitType) => {
  const isPetItem = code.includes('base-') || code.includes('additional-');
  const isPetPricing = code.includes('-dogs') || code.includes('-puppies') || code.includes('-cats');

  if (!isPetItem || !isPetPricing) {
    let label = humanizeLineItemCode(code);
    if (quantity && quantity > 1) {
      label += ` x ${quantity}`;
    }
    return label;
  }

  const petType = formatPetType(code);
  const pricingType = formatPricingType(code);
  const durationText = formatDuration(quantity, unitType);
  const totalSeats = seats || 1;

  if (code.includes('base-')) {
    return `${pricingType} ${formatPetCount(1, petType)}${durationText}`;
  } 
  
  if (code.includes('additional-')) {
    const additionalCount = totalSeats > 1 ? totalSeats - 1 : 1;
    return `Additional ${pricingType} ${formatPetCount(additionalCount, petType)}${durationText}`;
  }

  return humanizeLineItemCode(code);
};